// ============================================================
// CompLeavePage — 加班轉補休（§32-1）
// ============================================================
// 員工：查餘額 + 申請轉換 + 看歷史
// 主管/老闆：可看任一員工
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

interface Balance {
  year: number;
  totalOvertimeHours: number;
  totalConvertedHours: number;
  availableCompHours: number;
}

interface CompRecord {
  id: string;
  userId: string;
  hours: number;
  startDate: string;
  endDate: string;
  reason?: string;
  status: string;
  createdAt: string;
  user?: { id: string; name: string };
}

export function CompLeavePage() {
  const { user } = useAuthStore();
  const [balance, setBalance] = useState<Balance | null>(null);
  const [history, setHistory] = useState<CompRecord[]>([]);
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const year = new Date().getFullYear();
      const [bal, hist] = await Promise.all([
        api.get<Balance>(`/comp-leave/balance/${user.id}?year=${year}`),
        api.get<{ records: CompRecord[] }>('/comp-leave/history'),
      ]);
      setBalance(bal);
      setHistory(hist.records);
    } catch (err) {
      setMsg({ type: 'err', text: (err as Error).message });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConvert = async () => {
    setMsg(null);
    const h = Number(hours);
    if (!Number.isFinite(h) || h <= 0) {
      setMsg({ type: 'err', text: '請輸入有效時數' });
      return;
    }
    if (!balance || h > balance.availableCompHours) {
      setMsg({ type: 'err', text: `可轉時數不足（目前 ${balance?.availableCompHours ?? 0} 小時）` });
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ deadlineDays: number }>('/comp-leave/convert', {
        hours: h,
        note: note.trim() || undefined,
      });
      setMsg({ type: 'ok', text: `已轉換 ${h} 小時補休（期限 ${res.deadlineDays} 天）` });
      setHours(''); setNote('');
      fetchData();
    } catch (err) {
      setMsg({ type: 'err', text: (err as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-morandi-800">加班轉補休</h1>
        <p className="text-xs text-morandi-400 mt-1">勞基法 §32-1：可將加班時數轉為補休時數（1:1）</p>
      </div>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : balance ? (
        <>
          {/* 餘額卡片 */}
          <div className="grid grid-cols-3 gap-3">
            <Stat label="本年加班" value={balance.totalOvertimeHours} unit="hr" color="text-morandi-700" />
            <Stat label="已轉補休" value={balance.totalConvertedHours} unit="hr" color="text-amber-600" />
            <Stat label="可轉時數" value={balance.availableCompHours} unit="hr" color="text-sage-600" highlight />
          </div>

          {/* 申請表單 */}
          <div className="card-morandi p-5 space-y-3">
            <h2 className="font-semibold text-morandi-700">申請轉補休</h2>

            <div>
              <label className="text-sm text-morandi-500 block mb-1">時數（小時）</label>
              <input
                type="number"
                inputMode="decimal"
                value={hours}
                onChange={e => setHours(e.target.value)}
                placeholder={`最多 ${balance.availableCompHours} 小時`}
                className="input-field"
                step="0.5"
              />
            </div>

            <div>
              <label className="text-sm text-morandi-500 block mb-1">備註（選填）</label>
              <input
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="說明用途（如：5/1 加班 4 小時）"
                className="input-field"
                maxLength={200}
              />
            </div>

            {msg && (
              <div className={`text-sm px-3 py-2 rounded-lg ${msg.type === 'ok' ? 'bg-sage-100 text-sage-700' : 'bg-rose-100 text-rose-600'}`}>
                {msg.text}
              </div>
            )}

            <button
              onClick={handleConvert}
              disabled={submitting || !hours || balance.availableCompHours <= 0}
              className="w-full py-3 bg-earth-400 hover:bg-earth-500 disabled:opacity-50 text-white rounded-btn text-sm font-medium"
            >
              {submitting ? '處理中...' : '確認申請'}
            </button>

            <div className="text-xs text-morandi-400 bg-morandi-50 rounded-lg px-3 py-2">
              💡 補休期限：自申請日起 180 天內須使用，逾期未休則視為失效。
            </div>
          </div>

          {/* 歷史 */}
          <div className="card-morandi p-4">
            <h2 className="font-semibold text-morandi-700 mb-3">歷史紀錄</h2>
            {history.length === 0 ? (
              <div className="text-center py-6 text-morandi-300 text-sm">尚無紀錄</div>
            ) : (
              <div className="space-y-2">
                {history.map(r => (
                  <div key={r.id} className="flex items-start justify-between py-2 border-b border-morandi-100 last:border-b-0">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-morandi-700">
                        +{r.hours} 小時補休
                        {r.user && r.user.id !== user?.id && (
                          <span className="ml-2 text-xs text-morandi-400">{r.user.name}</span>
                        )}
                      </div>
                      <div className="text-xs text-morandi-400 mt-0.5">
                        {r.reason?.replace('[COMP] §32-1 加班轉補休；', '').replace(/（期限.+天）/, '') || '—'}
                      </div>
                      <div className="text-xs text-morandi-300 mt-0.5">
                        申請於 {new Date(r.createdAt).toLocaleDateString('zh-TW')} · 截止 {new Date(r.endDate).toLocaleDateString('zh-TW')}
                      </div>
                    </div>
                    <span className={`badge text-xs ${
                      r.status === 'APPROVED' ? 'bg-sage-100 text-sage-600' :
                      r.status === 'PENDING' ? 'bg-amber-100 text-amber-600' :
                      'bg-morandi-100 text-morandi-500'
                    }`}>
                      {r.status === 'APPROVED' ? '已核准' : r.status === 'PENDING' ? '待簽核' : r.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value, unit, color, highlight }: {
  label: string; value: number; unit: string; color: string; highlight?: boolean;
}) {
  return (
    <div className={`card-morandi p-3 text-center ${highlight ? 'ring-2 ring-sage-300' : ''}`}>
      <div className={`text-2xl font-bold font-mono ${color}`}>{value}<span className="text-xs ml-0.5 font-normal text-morandi-400">{unit}</span></div>
      <div className="text-xs text-morandi-400 mt-0.5">{label}</div>
    </div>
  );
}
